import { PHASES, passCost, passRefund } from '../constants';
import { cardById } from '../deck';

const clone = (state) => ({
  ...state,
  meta: { ...state.meta, seq: state.meta.seq + 1 },
});

const logged = (state, text) => ({
  ...state,
  log: [...state.log, { round: state.meta.round, phase: state.meta.phase, text }],
});

const money = (thousands) => `$${(thousands * 1000).toLocaleString('en-US')}`;

/** Players still bidding in the current auction round. */
export const activeBidders = (state) => state.players.filter((p) => !p.passed);

/** The highest bid currently on the table. */
export const highestBid = (state) => Math.max(0, ...state.players.map((p) => p.bid));

/**
 * The face-up house a passing player takes: IELLO p5, "the smaller value
 * home card still available at the center of the table".
 */
export const lowestInMarket = (state) => (
  state.market.reduce((low, id) => (
    !low || cardById(id).value < cardById(low).value ? id : low
  ), null)
);

/**
 * What a seat may legally do on its bidding turn. Passing is always legal; a
 * raise must beat the table's highest bid (IELLO p5: "The following players
 * must bid higher than the last one announced") and fit inside the player's
 * money ("If you do not have any more coins, you are forced to pass").
 *
 * Returns { canBid, min, max } with min/max in thousands.
 */
export const bidRange = (state, seat) => {
  const player = state.players[seat];
  const min = highestBid(state) + 1;
  const max = player.coins;
  return { canBid: min <= max, min, max };
};

/** The next seat clockwise that is still in the round. */
const nextActiveSeat = (state, fromSeat) => {
  const count = state.players.length;
  for (let step = 1; step <= count; step += 1) {
    const seat = (fromSeat + step) % count;
    if (!state.players[seat].passed) return seat;
  }
  return fromSeat;
};

/**
 * Deals the next round of whichever phase the decks say we are in.
 *
 * IELLO p3: "At the beginning of each round, draw as many home cards as
 * players and place them face up" - and p7 says the same of cheques. The
 * buying phase ends when the house deck is out (p5), and the game ends when
 * the cheque deck is out (p7) - that last transition happens in
 * saleActions.resolveSale, not here.
 */
export const startRound = (state) => {
  const count = state.players.length;
  let next = clone(state);
  next.meta.roundOver = null;

  if (next.propertyDeck.length > 0) {
    next.meta.phase = PHASES.BUYING;
    next.meta.round += 1;
    next.market = next.propertyDeck.slice(0, count);
    next.propertyDeck = next.propertyDeck.slice(count);
    next.meta.turnSeat = next.meta.openerSeat;
    next.players = next.players.map((p) => ({ ...p, bid: 0, passed: false }));
    return logged(next, `Auction ${next.meta.round}: ${next.market
      .map((id) => cardById(id).value)
      .sort((a, b) => a - b)
      .join(', ')} are up for sale.`);
  }

  // House deck is out: the sale phase. Reset the round counter the first
  // time through so sale rounds read 1..N in the log and the banner.
  if (next.meta.phase !== PHASES.SELLING) {
    next.meta.phase = PHASES.SELLING;
    next.meta.round = 0;
    next.meta.turnSeat = null;
  }
  next.meta.round += 1;
  next.market = next.chequeDeck.slice(0, count);
  next.chequeDeck = next.chequeDeck.slice(count);
  next.players = next.players.map((p) => ({ ...p, pick: null }));
  return logged(next, `Sale ${next.meta.round}: cheques for ${next.market
    .map((id) => cardById(id).value)
    .sort((a, b) => a - b)
    .map(money)
    .join(', ')} are on the table.`);
};

/**
 * IELLO p5 BID: raise the money in front of you to `amount` (total, in
 * thousands, not an increment).
 */
export const placeBid = (state, { seat, amount }) => {
  if (state.meta.phase !== PHASES.BUYING || seat !== state.meta.turnSeat) return state;
  const player = state.players[seat];
  if (!player || player.passed) return state;

  const { canBid, min, max } = bidRange(state, seat);
  if (!canBid || amount < min || amount > max || !Number.isInteger(amount)) return state;

  let next = clone(state);
  next.players = next.players.map((p) => (p.seat === seat ? { ...p, bid: amount } : p));
  next.meta.turnSeat = nextActiveSeat(next, seat);
  return logged(next, `${player.name} bid ${money(amount)}.`);
};

/**
 * IELLO p5 SPEND: pass, take the cheapest house still on the table, and get
 * back half your bid - with the Gryphon edition's rounding, refund =
 * floor(bid/2), forfeit = ceil(bid/2) (docs/RULES_SOURCES.md).
 *
 * When that leaves one bidder standing, the round ends in the same dispatch:
 * IELLO p5 END OF THE ROUND, the survivor pays their full bid and takes the
 * last house - which is always the round's highest, because every passer
 * before them took a lowest.
 */
export const pass = (state, { seat }) => {
  if (state.meta.phase !== PHASES.BUYING || seat !== state.meta.turnSeat) return state;
  const player = state.players[seat];
  if (!player || player.passed) return state;

  const taken = lowestInMarket(state);
  const paid = passCost(player.bid);

  let next = clone(state);
  next.market = next.market.filter((id) => id !== taken);
  next.players = next.players.map((p) => (p.seat === seat
    ? {
      ...p,
      passed: true,
      bid: 0,
      coins: p.coins - paid,
      properties: [...p.properties, taken],
    }
    : p));

  next = logged(next, player.bid > 0
    ? `${player.name} passed, took house ${cardById(taken).value}, and got back ${money(passRefund(player.bid))} of their ${money(player.bid)} bid.`
    : `${player.name} passed without bidding and took house ${cardById(taken).value} for free.`);

  const standing = activeBidders(next);
  if (standing.length > 1) {
    next.meta.turnSeat = nextActiveSeat(next, seat);
    return next;
  }

  // One bidder left: they pay in full and take the last house.
  const winner = standing[0];
  const lastHouse = next.market[0];
  next.market = [];
  next.players = next.players.map((p) => (p.seat === winner.seat
    ? {
      ...p,
      passed: true,
      bid: 0,
      coins: p.coins - winner.bid,
      properties: [...p.properties, lastHouse],
    }
    : p));
  next.meta.turnSeat = null;
  next.meta.openerSeat = winner.seat;
  next = logged(next, `${winner.name} won house ${cardById(lastHouse).value} for ${money(winner.bid)}.`);

  next.meta.roundOver = {
    kind: 'auction',
    round: next.meta.round,
    winnerSeat: winner.seat,
    // What everyone ended the round holding, newest purchase last.
    results: next.players.map((p) => ({
      seat: p.seat,
      name: p.name,
      property: p.properties[p.properties.length - 1],
    })),
    // Reading material for the banner: does the next deal open the sale?
    nextPhase: next.propertyDeck.length === 0 ? PHASES.SELLING : PHASES.BUYING,
  };
  return next;
};
